import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../context/userContext";
import { extractErrorMessage } from "../utils/apiError";
import { toast } from "../utils/toast";
import { GOOGLE_OAUTH_STATE_KEY, GOOGLE_OAUTH_RETURN_KEY, fetchGoogleProfileName } from "../utils/googleAuthRedirect";
import { DEFAULT_LANG } from "../utils/i18n";
import LoadingText from "../components/common/LoadingText";

// Return leg of the redirect fallback in GoogleSignInButton: Google lands
// the user back here (a real page load, no popup involved) with the id
// token in the URL fragment. Runs once, then bounces onward — there's
// nothing for the user to see here beyond a brief loading state.
export default function GoogleAuthCallbackPage() {
  const { loginWithGoogle } = useUser();
  const navigate = useNavigate();
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const params = new URLSearchParams(window.location.hash.slice(1));
    const idToken = params.get("id_token");
    const accessToken = params.get("access_token");
    const returnedState = params.get("state");
    const error = params.get("error") || new URLSearchParams(window.location.search).get("error");

    const expectedState = sessionStorage.getItem(GOOGLE_OAUTH_STATE_KEY);
    let returnInfo = { language: DEFAULT_LANG, returnPath: `/login/${DEFAULT_LANG}` };
    try {
      returnInfo = { ...returnInfo, ...JSON.parse(sessionStorage.getItem(GOOGLE_OAUTH_RETURN_KEY) || "{}") };
    } catch {
      // Malformed/missing — fall back to the defaults above.
    }
    sessionStorage.removeItem(GOOGLE_OAUTH_STATE_KEY);
    sessionStorage.removeItem(GOOGLE_OAUTH_RETURN_KEY);

    if (error || !idToken || !returnedState || returnedState !== expectedState) {
      if (error && error !== "access_denied") {
        toast.error("Google sign-in didn't go through. Please try again.");
      }
      navigate(returnInfo.returnPath, { replace: true });
      return;
    }

    loginWithGoogle(idToken)
      .then(async (me) => {
        if (me.is_onboarded) {
          navigate("/transcribe", { replace: true });
          return;
        }
        const googleFullName = me.full_name || (accessToken ? await fetchGoogleProfileName(accessToken).catch(() => null) : null);
        navigate(`/onboarding/${returnInfo.language}`, { replace: true, state: { googleFullName } });
      })
      .catch((err) => {
        toast.error(extractErrorMessage(err));
        navigate(returnInfo.returnPath, { replace: true });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="page-narrow auth-page">
      <div className="auth-content">
        <LoadingText text="Signing you in" />
      </div>
    </div>
  );
}
